import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQuery } from '@apollo/client'
import type { RowSelectionState } from '@tanstack/react-table'
import type { AlgoliaHit } from 'instantsearch.js'

import { useToast } from 'Components/Toast/useToast'

import { useStore } from 'Stores/index'

import { useActiveUser } from 'Hooks'

import { handleFetchErrorFunctional } from 'Utilities'

import { SentryErrorType } from 'Constants/errors'
import type {
  mutationAddTagsForRentals,
  mutationAddTagsForRentalsVariables,
} from 'Constants/graphql/mutations/__generated__/mutationAddTagsForRentals'
import type {
  mutationRemoveTagsForRentals,
  mutationRemoveTagsForRentalsVariables,
} from 'Constants/graphql/mutations/__generated__/mutationRemoveTagsForRentals'
import { MUTATION_ADD_TAGS_FOR_RENTALS } from 'Constants/graphql/mutations/MutationAddTagsForRentals'
import { MUTATION_REMOVE_TAGS_FOR_RENTALS } from 'Constants/graphql/mutations/MutationRemoveTagsForRentals'
import type {
  getTags,
  getTagsVariables,
} from 'Constants/graphql/queries/listing/__generated__/getTags'
import { GET_TAGS } from 'Constants/graphql/queries/listing/GetTags'

import type { SearchResultsDocument } from 'Types/globalSearch/searchResultsDocument.types'

import type { TAG_COLOR } from 'Portal/__generated__/globalTypes'

import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import type { Tag, TagColor } from './tagging.types'
import type { TaggingContextType } from './TaggingProvider.types'
import { INITIAL_STATE, taggingReducer } from './taggingReducer'
import { TagToastContent } from './TagToastContent'

interface UseTaggingTableOptions {
  hits: AlgoliaHit<SearchResultsDocument>[]
  refresh: () => void
}

export const useTaggingTable = (
  options: UseTaggingTableOptions,
): TaggingContextType => {
  const { hits, refresh } = options
  const { hideToast, showToast } = useToast()
  const { activeOrganisation } = useActiveUser()
  const { userStore } = useStore()
  const intl = useIntl()
  const orgId = String(activeOrganisation?.id ?? '')
  const userId = userStore.user.userId?.toString() ?? ''

  const [state, dispatch] = useReducer(taggingReducer, INITIAL_STATE)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [syncAction, setSyncAction] = useState<'add' | 'remove' | null>(null)

  const [executeAddTags, { loading: isAddingTag }] = useMutation<
    mutationAddTagsForRentals,
    mutationAddTagsForRentalsVariables
  >(MUTATION_ADD_TAGS_FOR_RENTALS)

  const [executeRemoveTags, { loading: isRemovingTag }] = useMutation<
    mutationRemoveTagsForRentals,
    mutationRemoveTagsForRentalsVariables
  >(MUTATION_REMOVE_TAGS_FOR_RENTALS)

  const { data: tagsData, refetch: refetchTags } = useQuery<
    getTags,
    getTagsVariables
  >(GET_TAGS, {
    skip: !orgId,
    variables: { organisationID: orgId },
  })

  const orgTags: Tag[] = useMemo(
    () =>
      (tagsData?.tags ?? []).map(t => ({
        color: t.color,
        id: t.id,
        name: t.name,
      })),
    [tagsData],
  )

  const autocompleteRef = useRef<TagAutocompleteHandle | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const handleSubmitRef = useRef<() => void>(() => {})
  const justEnteredEditRef = useRef(false)
  const pendingRefreshRef = useRef(false)
  const prevModeRef = useRef(state.mode)
  const prevSelectionRef = useRef<RowSelectionState>(rowSelection)
  const orgTagsRef = useRef<Tag[]>(orgTags)
  const hitsRef = useRef(hits)

  orgTagsRef.current = orgTags
  hitsRef.current = hits

  const handleMutationError = useCallback(
    (operationName: string, messageId: string) =>
      (error: Error, options?: { variables?: unknown }) => {
        showToast({
          content: intl.formatMessage({ id: messageId }),
          variant: 'error',
        })
        handleFetchErrorFunctional()(error, {
          location: window?.location || undefined,
          operation: {
            operationName,
            variables: options?.variables,
          },
          type: SentryErrorType.GQL_ERROR,
          userId,
        })
      },
    [intl, showToast, userId],
  )

  // --- Dispatch wrappers ---

  const enterEditMode = useCallback((rowId: string) => {
    dispatch({ payload: { rowId }, type: 'ENTER_EDIT_MODE' })
  }, [])

  const exitEditMode = useCallback(() => {
    dispatch({ type: 'EXIT_EDIT_MODE' })
  }, [])

  const setInputText = useCallback((text: string) => {
    dispatch({ payload: { text }, type: 'SET_INPUT_TEXT' })
  }, [])

  const setSelectedColor = useCallback((color: TagColor) => {
    dispatch({ payload: { color }, type: 'SET_SELECTED_COLOR' })
  }, [])

  const setActiveEditorRowId = useCallback((rowId: string | null) => {
    dispatch({ payload: { rowId }, type: 'SET_ACTIVE_EDITOR_ROW_ID' })
  }, [])

  const addTag = useCallback(
    (rentalIds: string[], name: string, color: TagColor) => {
      if (!orgId) {
        return
      }

      executeAddTags({
        onCompleted: data => {
          const affectedItems = data.addTagsForRentals.items ?? []
          const affectedRentalIds = Array.from(
            new Set(affectedItems.map(item => String(item.taggable_id))),
          )

          if (affectedRentalIds.length === 0) {
            return
          }

          setSyncAction('add')
          pendingRefreshRef.current = true
          refresh()

          const refetchPromise = refetchTags()

          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: 'Tags_added' },
                  { count: affectedRentalIds.length, name },
                )}
                onUndo={async () => {
                  await refetchPromise

                  const tagId = orgTagsRef.current.find(
                    t => t.name === name,
                  )?.id

                  if (!tagId) {
                    return
                  }

                  hideToast()
                  executeRemoveTags({
                    onCompleted: () => {
                      setSyncAction('remove')
                      pendingRefreshRef.current = true
                      refresh()
                      refetchTags()
                    },
                    onError: handleMutationError('removeTagsForRentals', 'Tags_remove_error'),
                    variables: {
                      input: {
                        orgID: orgId,
                        rentalIDs: affectedRentalIds,
                        tagIDs: [tagId],
                      },
                    },
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: handleMutationError('addTagsForRentals', 'Tags_add_error'),
        variables: {
          input: {
            orgID: orgId,
            rentalIDs: rentalIds,
            tags: [{ color: color as TAG_COLOR, name }],
          },
        },
      })
    },
    [orgId, executeAddTags, executeRemoveTags, handleMutationError, hideToast, intl, refetchTags, refresh, showToast],
  )

  const removeTag = useCallback(
    (rentalIds: string[], tag: Tag) => {
      if (!orgId) {
        return
      }

      const affectedRentalIds = rentalIds.filter(rentalId => {
        const hit = hitsRef.current.find(h => String(h.id) === rentalId)
        const rowTags = hit?.['rental.tags'] ?? []

        return rowTags.some(t => String(t.id) === tag.id)
      })

      if (affectedRentalIds.length === 0) {
        return
      }

      executeRemoveTags({
        onCompleted: () => {
          setSyncAction('remove')
          pendingRefreshRef.current = true
          refresh()
          refetchTags()
          showToast({
            content: (
              <TagToastContent
                message={intl.formatMessage(
                  { id: 'Tags_removed' },
                  { count: affectedRentalIds.length, name: tag.name },
                )}
                onUndo={() => {
                  hideToast()
                  executeAddTags({
                    onCompleted: () => {
                      setSyncAction('add')
                      pendingRefreshRef.current = true
                      refresh()
                      refetchTags()
                    },
                    onError: handleMutationError('addTagsForRentals', 'Tags_add_error'),
                    variables: {
                      input: {
                        orgID: orgId,
                        rentalIDs: affectedRentalIds,
                        tags: [{ color: tag.color as TAG_COLOR, name: tag.name }],
                      },
                    },
                  })
                }}
              />
            ),
            variant: 'success',
          })
        },
        onError: handleMutationError('removeTagsForRentals', 'Tags_remove_error'),
        variables: {
          input: {
            orgID: orgId,
            rentalIDs: affectedRentalIds,
            tagIDs: [tag.id],
          },
        },
      })
    },
    [orgId, executeAddTags, executeRemoveTags, handleMutationError, hideToast, intl, refetchTags, refresh, showToast],
  )

  const isEditorOpen = state.mode === 'edit' && state.activeEditorRowId !== null
  const isMutating = isAddingTag || isRemovingTag || !!syncAction

  // Guards external callers (TanStack/MobX); internal effects use setRowSelection directly
  const guardedSetRowSelection: typeof setRowSelection = useCallback(
    updater => {
      if (isMutating) {
        return
      }

      setRowSelection(updater)
    },
    [isMutating],
  )

  // --- Derived selection state ---

  const selectedRowIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection],
  )

  const selectedCount = selectedRowIds.length

  const isRowSelected = useCallback(
    (rowId: string) => !!rowSelection[rowId],
    [rowSelection],
  )

  // --- Selection sync effects ---

  // Effect 1: auto-check triggerRowId when entering edit mode
  useEffect(() => {
    if (state.mode === 'edit' && state.triggerRowId) {
      justEnteredEditRef.current = true
      setRowSelection(prev => ({ ...prev, [state.triggerRowId!]: true }))
    }
  }, [state.mode, state.triggerRowId])

  // Effect 2: clear selection on edit -> read transition
  useEffect(() => {
    if (prevModeRef.current === 'edit' && state.mode === 'read') {
      setRowSelection({})
    }

    prevModeRef.current = state.mode
  }, [state.mode])

  // Effect 3: uncheck-all guard - exit edit mode if no rows selected
  useEffect(() => {
    if (justEnteredEditRef.current) {
      justEnteredEditRef.current = false

      return
    }

    const hasSelectedRows = Object.values(rowSelection).some(Boolean)

    if (state.mode === 'edit' && !hasSelectedRows) {
      exitEditMode()
    }
  }, [rowSelection, state.mode, exitEditMode])

  // Effect 4: reassign editor row when active row is unchecked
  useEffect(() => {
    if (state.mode !== 'edit' || !state.activeEditorRowId) {
      prevSelectionRef.current = rowSelection

      return
    }

    const wasSelected = prevSelectionRef.current[state.activeEditorRowId]
    const isSelected = rowSelection[state.activeEditorRowId]

    if (wasSelected && !isSelected) {
      const currentSelectedIds = Object.keys(rowSelection).filter(
        id => rowSelection[id],
      )

      if (currentSelectedIds.length > 0) {
        const rowOrder = hits.map(h => String(h.id))
        const currentIndex = rowOrder.indexOf(state.activeEditorRowId)
        let nextRowId: string | null = null

        for (let i = currentIndex + 1; i < rowOrder.length; i += 1) {
          if (currentSelectedIds.includes(rowOrder[i])) {
            nextRowId = rowOrder[i]
            break
          }
        }

        if (!nextRowId) {
          for (let i = 0; i < currentIndex; i += 1) {
            if (currentSelectedIds.includes(rowOrder[i])) {
              nextRowId = rowOrder[i]
              break
            }
          }
        }

        if (nextRowId) {
          setActiveEditorRowId(nextRowId)
        }
      }
    }

    prevSelectionRef.current = rowSelection
  }, [
    rowSelection,
    state.mode,
    state.activeEditorRowId,
    setActiveEditorRowId,
    hits,
  ])

  // Effect 5: complete sync after refresh brings fresh hits
  useEffect(() => {
    if (!syncAction || !pendingRefreshRef.current) {
      return
    }

    pendingRefreshRef.current = false

    if (syncAction === 'add') {
      dispatch({ type: 'EXIT_EDIT_MODE' })
    }

    setSyncAction(null)
  }, [hits]) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Actions ---

  const canSubmit = state.inputText.trim().length > 0

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isAddingTag) {
      return
    }

    const trimmed = state.inputText.trim()
    const exactMatch = orgTags.find(t => t.name.toLowerCase() === trimmed)
    const color = exactMatch ? exactMatch.color : state.selectedColor

    addTag(selectedRowIds, trimmed, color)
  }, [
    canSubmit,
    isAddingTag,
    orgTags,
    selectedRowIds,
    state.inputText,
    state.selectedColor,
    addTag,
  ])

  handleSubmitRef.current = handleSubmit

  // --- Keyboard handler ---

  useEffect(() => {
    if (state.mode !== 'edit') {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        state.inputText.trim() &&
        autocompleteRef.current?.hasSuggestions()
      ) {
        autocompleteRef.current.clearSuggestions()
        inputRef.current?.focus()

        return
      }

      if (
        e.key === 'ArrowDown' &&
        document.activeElement === inputRef.current &&
        autocompleteRef.current?.hasSuggestions()
      ) {
        e.preventDefault()
        autocompleteRef.current.focusFirst()

        return
      }

      if (
        document.activeElement === inputRef.current &&
        autocompleteRef.current?.handleKeyDown(e)
      ) {
        return
      }

      if (
        e.key === 'Enter' &&
        document.activeElement === inputRef.current
      ) {
        e.preventDefault()
        handleSubmitRef.current()

        return
      }

      if (e.key === 'Escape' && !isAddingTag && !isRemovingTag) {
        exitEditMode()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [state.mode, state.inputText, exitEditMode, isAddingTag, isRemovingTag])

  // --- Outside click dismiss (when input is empty) ---

  useEffect(() => {
    if (state.mode !== 'edit') {
      return
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (isAddingTag || isRemovingTag) {
        return
      }

      if (state.inputText.trim()) {
        return
      }

      const target = e.target as HTMLElement | null

      if (target?.closest('.TaggingTable .Table-row')) {
        return
      }

      exitEditMode()
    }

    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [state.mode, state.inputText, exitEditMode, isAddingTag, isRemovingTag])

  return {
    ...state,
    addTag,
    autocompleteRef,
    canSubmit,
    enterEditMode,
    exitEditMode,
    handleSubmit,
    inputRef,
    isAddingTag,
    isEditorOpen,
    isRemovingTag,
    isRowSelected,
    orgTags,
    removeTag,
    rowSelection,
    selectedCount,
    selectedRowIds,
    setActiveEditorRowId,
    setInputText,
    setRowSelection: guardedSetRowSelection,
    setSelectedColor,
  }
}
